import React, { useState } from 'react'
import {
  Typography,
  withStyles,
  InputAdornment,
  MenuItem,
} from '@material-ui/core'
import {
  CoreTextField,
  CoreIconButton,
  CoreToggleButtonGroup,
  CoreToggleButton,
} from 'src/components/core'
import { Icon } from '@iconify/react'
import styles from '../styles'
import {
  EMAIL_PROVIDER,
  PROVIDER_LABELS,
  SMTP_PORTS,
  SMTP_PRESETS,
  SMTP_MAILBOX_NOTE,
  SMTP_PRESET_CUSTOM,
  SMTP_PRESET_HINT,
  SMTP_SECURITY_OPTIONS,
} from '../constants'
import { findSmtpPreset, isValidSmtpHost } from '../utils'

const EmailSettings = ({
  classes,
  apiToken,
  onApiTokenChange,
  sendingDomain,
  onSendingDomainChange,
  senderName,
  onSenderNameChange,
  isValidEmail,
  provider,
  smtp,
  onSmtpChange,
  onSmtpHostBlur,
  onSmtpPresetSelect,
  smtpHasPassword,
}) => {
  const [showSecret, setShowSecret] = useState(false)
  // Custom is the one choice with no host of its own to recognise it by.
  const [customChosen, setCustomChosen] = useState(false)

  const handleShowSecret = () => {
    setShowSecret(!showSecret)
  }

  const isSmtp = provider === EMAIL_PROVIDER.SMTP

  // The placeholder only surfaces once the field has focus, so the expected
  // format lives in helper text that is visible before anyone clicks.
  const hostHasError = smtp.host.length > 0 && !isValidSmtpHost(smtp.host)

  const activePreset = findSmtpPreset(smtp.host)
  const presetValue =
    activePreset?.id ?? (smtp.host || customChosen ? SMTP_PRESET_CUSTOM : null)

  const handlePresetChange = (event, id) => {
    // An exclusive group reports null when the active button is clicked again;
    // there is no "no provider" state to fall back to, so keep the selection.
    if (!id) return
    setCustomChosen(id === SMTP_PRESET_CUSTOM)
    onSmtpPresetSelect(SMTP_PRESETS.find(preset => preset.id === id) || null)
  }

  const secretVisibilityAdornment = (
    <InputAdornment position="end">
      <CoreIconButton
        onClick={handleShowSecret}
        edge="end"
        color="dark"
        variant="text"
      >
        {showSecret ? <Icon icon="mdi:eye" /> : <Icon icon="mdi:eye-off" />}
      </CoreIconButton>
    </InputAdornment>
  )

  return (
    <div className={classes.settingsContainer}>
      <Typography className={classes.settingsTitle}>
        {PROVIDER_LABELS[provider]} Settings
      </Typography>
      <div className={classes.settingsFields}>
        {/* The address is the one thing every user knows without looking it up,
            and for a consumer mailbox it answers the provider question too, so
            it leads rather than trailing the connection settings. */}
        <CoreTextField
          label="Sending email address"
          placeholder="noreply@email.com"
          value={sendingDomain}
          onChange={onSendingDomainChange}
          fullWidth
          error={sendingDomain.length > 0 && !isValidEmail(sendingDomain)}
          helperText={
            sendingDomain.length > 0 && !isValidEmail(sendingDomain)
              ? 'Please enter a valid email address'
              : 'You can use your personal email if you don’t have a domain.'
          }
        />
        <CoreTextField
          placeholder="No-reply"
          label="Sender name"
          value={senderName}
          onChange={onSenderNameChange}
          fullWidth
          error={senderName.length > 0 && senderName.trim().length < 2}
          helperText={
            senderName.length > 0 && senderName.trim().length < 2
              ? 'Sender name should be at least 2 characters'
              : ''
          }
        />
        {isSmtp ? (
          <>
            <div>
              <CoreToggleButtonGroup
                value={presetValue}
                onChange={handlePresetChange}
                exclusive
                fullWidth
                label="WHERE IS YOUR EMAIL HOSTED?"
              >
                {SMTP_PRESETS.map(preset => (
                  <CoreToggleButton key={preset.id} value={preset.id}>
                    {preset.label}
                  </CoreToggleButton>
                ))}
                <CoreToggleButton value={SMTP_PRESET_CUSTOM}>
                  Other
                </CoreToggleButton>
              </CoreToggleButtonGroup>
              <Typography className={classes.presetHint}>
                {SMTP_PRESET_HINT}
              </Typography>
              <Typography className={classes.presetNote}>
                {SMTP_MAILBOX_NOTE}
              </Typography>
            </div>
            <CoreTextField
              label="SMTP server"
              placeholder="smtp.gmail.com"
              value={smtp.host}
              onChange={e => onSmtpChange('host', e.target.value)}
              onBlur={onSmtpHostBlur}
              fullWidth
              error={hostHasError}
              helperText={
                hostHasError
                  ? 'Enter the server hostname only. No https://, no port, no email address.'
                  : 'The server address your email host gave you, e.g. smtp.gmail.com or smtp.office365.com. It is usually their domain, not yours.'
              }
            />
            <div className={classes.settingsRow}>
              <CoreTextField
                label="Security"
                value={smtp.security}
                onChange={e =>
                  onSmtpChange('security', parseInt(e.target.value))
                }
                fullWidth
                select
              >
                {SMTP_SECURITY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </CoreTextField>
              <CoreTextField
                label="Port"
                value={smtp.port}
                onChange={e => onSmtpChange('port', parseInt(e.target.value))}
                fullWidth
                select
              >
                {SMTP_PORTS.map(port => (
                  <MenuItem key={port} value={port}>
                    {port}
                  </MenuItem>
                ))}
              </CoreTextField>
            </div>
            <CoreTextField
              label="Username"
              placeholder="noreply@yourdomain.com"
              value={smtp.username}
              onChange={e => onSmtpChange('username', e.target.value)}
              fullWidth
              helperText="The login for that mailbox, usually the full email address"
            />
            <CoreTextField
              label="Password"
              placeholder={
                smtpHasPassword ? 'Leave blank to keep current password' : ''
              }
              value={smtp.password}
              onChange={e => onSmtpChange('password', e.target.value)}
              fullWidth
              type={showSecret ? 'text' : 'password'}
              helperText={
                smtpHasPassword && !smtp.password
                  ? 'A password is already saved. Type a new one to replace it.'
                  : activePreset?.passwordHint || ''
              }
              InputProps={{ endAdornment: secretVisibilityAdornment }}
            />
          </>
        ) : (
          <CoreTextField
            placeholder="sk-abc123..."
            label={`${PROVIDER_LABELS[provider]} API Token`}
            value={apiToken}
            onChange={onApiTokenChange}
            fullWidth
            type={showSecret ? 'text' : 'password'}
            error={apiToken.length > 0 && apiToken.length < 10}
            helperText={
              apiToken.length > 0 && apiToken.length < 10
                ? 'API token should be at least 10 characters'
                : ''
            }
            InputProps={{ endAdornment: secretVisibilityAdornment }}
          />
        )}
      </div>
    </div>
  )
}

export default withStyles(styles)(EmailSettings)
